import React, { useMemo, useState } from 'react';
import { Button } from '../components/ui/Button';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/ToastProvider';
import { DispatchTimeline } from '../features/dispatch/DispatchTimeline';
import { TECHNICIANS } from '../features/dispatch/mockData';
import { StatusQueue } from '../features/dispatch/StatusQueue';
import { TechnicianPanel } from '../features/dispatch/TechnicianPanel';
import { useDispatchBoard } from '../features/dispatch/useDispatchBoard';

const RefreshIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M11.5 6.5A5 5 0 1 1 9 2.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    <path d="M9 1v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BellIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M6.5 1.5a4 4 0 0 1 4 4v2.5l1 1.5H1.5L2.5 8V5.5a4 4 0 0 1 4-4Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    <path d="M5 10.5a1.5 1.5 0 0 0 3 0" stroke="currentColor" strokeWidth="1.3"/>
  </svg>
);

const KPI_CONFIGS = [
  { key: 'incoming',   label: 'Incoming Queue', color: 'var(--text-primary)' },
  { key: 'scheduled',  label: 'Scheduled',      color: 'var(--accent)' },
  { key: 'openSlots',  label: 'Open Slots',     color: 'var(--text-primary)' },
  { key: 'onTimeRate', label: 'On-time Rate',   color: 'var(--success)' },
];

export function DispatchCommandCenterPage() {
  const { queue, timeline, assignToSlot, unassignFromSlot } = useDispatchBoard();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { pushToast } = useToast();

  const refreshBoard = () => {
    setLoading(true);
    setError('');
    window.setTimeout(() => {
      pushToast('Dispatch board refreshed', 'success');
      setLoading(false);
    }, 600);
  };

  const kpi = useMemo(() => {
    const assigned = timeline.filter((slot) => slot.job).length;
    return {
      incoming:   queue.length,
      scheduled:  assigned,
      openSlots:  timeline.length - assigned,
      onTimeRate: '94%',
    };
  }, [queue.length, timeline]);

  const handleAssign = (jobId, slotId) => {
    assignToSlot(jobId, slotId);
    pushToast(`Assigned ${jobId} to ${slotId.replace('slot-', '')}`, 'success');
  };

  const handleUnassign = (slotId) => {
    unassignFromSlot(slotId);
    pushToast(`Returned job from ${slotId.replace('slot-', '')}`, 'info');
  };

  return (
    <section className="dispatch-page">
      {/* KPI strip */}
      <div className="dispatch-kpi-grid" aria-label="Dispatch KPIs">
        {KPI_CONFIGS.map(({ key, label, color }) => (
          <article className="dispatch-kpi-card" key={key}>
            <h2>{label}</h2>
            <p style={{ color }}>{kpi[key]}</p>
          </article>
        ))}
      </div>

      {/* Toolbar */}
      <div className="dispatch-page-actions">
        <Button onClick={refreshBoard} disabled={loading}>
          <RefreshIcon />
          Refresh
        </Button>
        <Button variant="secondary" onClick={() => pushToast(`${queue.length} queued jobs available`, 'info')}>
          <BellIcon />
          Notify Supervisors
        </Button>
      </div>

      {error ? <ErrorState message={error} onRetry={refreshBoard} /> : null}
      {loading ? <Skeleton lines={6} /> : null}

      {!loading && !error && (
        <div className="dispatch-layout-grid">
          <StatusQueue jobs={queue} timeline={timeline} onAssign={handleAssign} />
          <DispatchTimeline timeline={timeline} onDropJob={handleAssign} onUnassign={handleUnassign} />
          <TechnicianPanel technicians={TECHNICIANS} />
        </div>
      )}
    </section>
  );
}
